// client/src/components/configuracoes/plataformaGoogleNegocio/PlataformaGoogleNegocioConfigPage.js
// Client ID/Secret do app OAuth do Google Meu Negócio da plataforma —
// usado por toda Conta que conectar seu próprio Perfil da Empresa (fluxo
// completo fica no app mobile, exclusivo de Conta). Espelha
// PlataformaNotaFiscalConfigPage.js.
import React, { useState, useEffect, useCallback } from 'react';
import { Building2, Save, Info, Copy } from 'lucide-react';
import PageHeader from '../../layout/PageHeader';
import useAlert from '../../../hooks/useAlert';
import { usePlataformaGoogleNegocioConfig, useSalvarPlataformaGoogleNegocioConfig } from '../../../hooks/usePlataformaGoogleNegocio';

const ToggleSwitch = ({ checked, onChange, label }) => (
    <label style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', cursor: 'pointer' }}>
        <div
            onClick={() => onChange(!checked)}
            style={{
                width: 44, height: 24, borderRadius: 12, cursor: 'pointer', position: 'relative', flexShrink: 0,
                background: checked ? 'var(--color-primary)' : '#d1d5db',
                transition: 'background 0.2s',
            }}
        >
            <div style={{
                position: 'absolute', top: 2, width: 20, height: 20, borderRadius: '50%',
                background: '#fff', boxShadow: '0 1px 3px rgba(0,0,0,0.3)',
                left: checked ? 22 : 2,
                transition: 'left 0.2s',
            }} />
        </div>
        <span style={{ fontWeight: 500, fontSize: '0.875rem' }}>{label}</span>
    </label>
);

const PlataformaGoogleNegocioConfigPage = () => {
    const { data: config, isLoading } = usePlataformaGoogleNegocioConfig();
    const salvarConfig = useSalvarPlataformaGoogleNegocioConfig();
    const { setAlert } = useAlert();

    const [clientId, setClientId] = useState('');
    const [clientSecret, setClientSecret] = useState('');
    const [ativo, setAtivo] = useState(false);

    useEffect(() => {
        if (config) {
            setClientId(config.clientId || '');
            setAtivo(config.ativo ?? false);
        }
    }, [config]);

    const handleSubmit = useCallback((e) => {
        e.preventDefault();
        const payload = { clientId, ativo };
        if (clientSecret) payload.clientSecret = clientSecret;
        salvarConfig.mutate(payload, { onSuccess: () => setClientSecret('') });
    }, [clientId, clientSecret, ativo, salvarConfig]);

    const copiarRedirectUri = () => {
        if (!config?.redirectUri) return;
        navigator.clipboard.writeText(config.redirectUri);
        setAlert('URL de callback copiada.', 'success');
    };

    if (isLoading) return null;

    return (
        <div className="page-management-container">
            <PageHeader icon={<Building2 />} title="Google Meu Negócio" subtitle="Credenciais OAuth da plataforma — cada Conta conecta seu próprio Perfil da Empresa" />
            <form onSubmit={handleSubmit}>
                <div className="content-panel">
                    <div className="form-section">
                        <h4 className="form-section-title">Status</h4>
                        <div className="form-group">
                            <ToggleSwitch checked={ativo} onChange={setAtivo} label={ativo ? 'Integração ativa' : 'Integração desativada'} />
                        </div>
                        <p className="form-hint" style={{ display: 'flex', alignItems: 'center', gap: 6, color: 'var(--color-text-secondary)', fontSize: '0.85rem' }}>
                            <Info size={14} /> Enquanto desativada, nenhuma Conta consegue conectar — o botão "Conectar" no app mostra um erro claro em vez de travar.
                        </p>
                    </div>

                    <div className="form-section">
                        <h4 className="form-section-title">Credenciais OAuth</h4>
                        <div className="form-grid-container-cols-2">
                            <div className="form-group">
                                <label className="form-label required">Client ID</label>
                                <input className="form-input" value={clientId} onChange={(e) => setClientId(e.target.value)} placeholder="xxxxxxxxxx.apps.googleusercontent.com" />
                            </div>
                            <div className="form-group">
                                <label className="form-label">Client Secret</label>
                                <input
                                    type="password"
                                    className="form-input"
                                    placeholder={config?.temClientSecret ? 'Já configurado — deixe em branco pra manter' : 'Digite o Client Secret'}
                                    value={clientSecret}
                                    onChange={(e) => setClientSecret(e.target.value)}
                                />
                            </div>
                        </div>
                        <p className="form-hint" style={{ display: 'flex', alignItems: 'center', gap: 6, color: 'var(--color-text-secondary)', fontSize: '0.85rem' }}>
                            <Info size={14} /> Gerados em console.cloud.google.com — passo a passo completo em server/README-google-negocio.md.
                        </p>
                    </div>

                    <div className="form-section">
                        <h4 className="form-section-title">URL de Callback</h4>
                        <div className="form-group">
                            <label className="form-label">Cadastre exatamente esta URL em "Authorized redirect URIs" no Google Cloud Console</label>
                            <div style={{ display: 'flex', gap: '0.5rem' }}>
                                <input className="form-input" value={config?.redirectUri || 'API_BASE_URL não configurada no servidor'} readOnly style={{ background: 'var(--color-bg-light)', fontFamily: 'monospace', fontSize: '0.85rem' }} />
                                <button type="button" className="action-button action-button-secondary" onClick={copiarRedirectUri} disabled={!config?.redirectUri}>
                                    <Copy size={18} className="icon" />
                                </button>
                            </div>
                        </div>
                    </div>

                    <div className="form-actions">
                        <button type="submit" className="action-button action-button-primary" disabled={salvarConfig.isPending}>
                            <Save size={18} className="icon" /> {salvarConfig.isPending ? 'Salvando...' : 'Salvar'}
                        </button>
                    </div>
                </div>
            </form>
        </div>
    );
};

export default PlataformaGoogleNegocioConfigPage;
